import { createClient, type Session } from "@supabase/supabase-js";
import { classifyRestaurantInput } from "./restaurant-input";
import { splitBookedPlans } from "./date-plans";
import { destroyRestaurantMap, mountRestaurantMap, type RestaurantMapItem } from "./restaurant-map";
import { validCoordinates } from "./restaurant-map-data";
import { buildCalendarEvent, zonedDateTimeToUtc } from "../supabase/functions/date-notifications/calendar";
import "./styles.css";

type User = { id: string; name: string };
type City = { id: string; name: string };
type Coordinates = { latitude: number; longitude: number };
type OpeningHours = { openNow?: boolean; weekdayDescriptions?: string[] };

type Restaurant = {
  id: string;
  cityId: string;
  ownerId: string;
  sourceUrl: string;
  resolvedUrl?: string | null;
  destinationKey: string;
  googlePlaceId?: string | null;
  googleMapsUrl?: string | null;
  coordinates?: Coordinates | null;
  timeZone?: string | null;
  name: string;
  city: string;
  neighborhood?: string | null;
  cuisine?: string | null;
  primaryType?: string | null;
  priceRange?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  websiteUrl?: string | null;
  rating?: number | null;
  userRatingCount?: number | null;
  regularOpeningHours?: OpeningHours | null;
  reservable?: boolean | null;
  businessStatus?: string | null;
  personalNote?: string | null;
  status: "active" | "visited" | "removed";
  createdAt: string;
  openPlan?: {
    id: string;
    tableId: string;
    stage: "pending_booking" | "booked";
    proposedDate: string;
    meal: "lunch" | "dinner";
  } | null;
};

type DatePlan = {
  id: string;
  proposerId: string;
  partnerId: string;
  restaurantId: string;
  proposedDate: string;
  meal: "lunch" | "dinner";
  timeStart?: string | null;
  timeEnd?: string | null;
  stage: "pending_booking" | "booked" | "completed" | "canceled";
  reservationTime?: string | null;
  timeZone?: string | null;
  bookingNote: string;
  changeRequestedBy?: string | null;
  changeRequestedDate?: string | null;
  changeRequestedTime?: string | null;
  changeRequestNote?: string | null;
  changeRequestedAt?: string | null;
  reactions: Record<string, string>;
  notes: Record<string, string>;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

type PlaceResult = Omit<Restaurant, "id" | "cityId" | "ownerId" | "status" | "createdAt" | "personalNote">;

type TableSummary = {
  id: string;
  name: string;
  defaultCity?: string | null;
  memberIds: string[];
  needsAction: boolean;
  createdAt: string;
};

type Partnership = {
  id: string;
  name: string;
  defaultCity?: string | null;
  memberIds: string[];
  inviteCode?: string | null;
  cities: Record<string, City>;
  restaurants: Record<string, Restaurant>;
  plans: Record<string, DatePlan>;
};

type AppState = {
  currentUserId: string;
  users: Record<string, User>;
  tables: Record<string, TableSummary>;
  partnership: Partnership | null;
};

type UiState = {
  loading: boolean;
  authMode: "create" | "join";
  authStep: "email" | "code";
  pendingEmail: string;
  modal: string | null;
  cityId: string | null;
  filter: "all" | string;
  boardView: "list" | "map";
  mapError: boolean;
  boardSearch: string;
  placeQuery: string;
  placeResults: PlaceResult[];
  placeSearchLoading: boolean;
  placeSearchMessage: string;
  selectedPlace: PlaceResult | null;
  selectedRestaurantId: string | null;
  selectedPlanId: string | null;
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error("Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY before starting NextTable.");
}

const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

const app = document.querySelector<HTMLElement>("#app")!;
const toastRegion = document.querySelector<HTMLElement>("#toast-region")!;
const LAST_TABLE_KEY = "nexttable:last-table";
const BOARD_VIEW_KEY = "nexttable:restaurant-view";
const inviteCodeFromUrl = () => new URLSearchParams(location.search).get("invite") || "";
const tableIdFromPath = () => location.pathname.match(/^\/t\/([0-9a-f-]{36})\/?$/i)?.[1] || null;

const ui: UiState = {
  loading: true,
  authMode: inviteCodeFromUrl() ? "join" : "create",
  authStep: "email",
  pendingEmail: "",
  modal: null,
  cityId: null,
  filter: "all",
  boardView: localStorage.getItem(BOARD_VIEW_KEY) === "map" ? "map" : "list",
  mapError: false,
  boardSearch: "",
  placeQuery: "",
  placeResults: [],
  placeSearchLoading: false,
  placeSearchMessage: "",
  selectedPlace: null,
  selectedRestaurantId: null,
  selectedPlanId: null,
};

let model: AppState | null = null;
let session: Session | null = null;
let placeSearchTimer: number | undefined;
let placeSearchSequence = 0;

const escapeHtml = (value: unknown = "") => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const errorMessage = (error: unknown) => error instanceof Error ? error.message : "Something went wrong.";
const currentUser = () => model?.users[model.currentUserId];
const partner = () => {
  const partnerId = partnership()?.memberIds.find((id) => id !== model?.currentUserId);
  return partnerId ? model?.users[partnerId] : undefined;
};
const partnership = () => model?.partnership;
const tables = () => Object.values(model?.tables || {});
const restaurants = () => Object.values(partnership()?.restaurants || {});
const cities = () => Object.values(partnership()?.cities || {});
const plans = () => Object.values(partnership()?.plans || {});
const myPendingPlan = () => plans().find((plan) => plan.stage === "pending_booking" && plan.proposerId === model?.currentUserId);
const bookingGroups = () => splitBookedPlans(plans());
const completedPlans = () => plans().filter((plan) => plan.stage === "completed").sort((a, b) => (b.completedAt || "").localeCompare(a.completedAt || ""));
const activeRestaurants = () => restaurants().filter((restaurant) => restaurant.status === "active");
const mySavedCount = () => activeRestaurants().filter((restaurant) => restaurant.ownerId === model?.currentUserId).length;
const initials = (name = "") => name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2);
const citySlug = (city = "") => city.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/,\s*(ca|ny|tx|il|wa|or|usa|united states)$/i, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function tablePartner(table: TableSummary | Partnership) {
  const id = table.memberIds.find((memberId) => memberId !== model?.currentUserId);
  return id ? model?.users[id] : undefined;
}

function tableLabel(table: TableSummary | Partnership) {
  const other = tablePartner(table);
  return other ? `With ${other.name}` : "Waiting for someone";
}

function activeTableId() {
  const id = partnership()?.id;
  if (!id) throw new Error("Choose a table first.");
  return id;
}

function resetBookingSelection() {
  ui.selectedPlanId = bookingGroups().future[0]?.id || null;
}

function preferredTableId() {
  return tableIdFromPath() || localStorage.getItem(LAST_TABLE_KEY);
}

function rememberTable(id: string, replace = true) {
  localStorage.setItem(LAST_TABLE_KEY, id);
  const next = `/t/${id}`;
  if (location.pathname !== next || location.search) {
    history[replace ? "replaceState" : "pushState"]({}, "", next);
  }
}

function icon(name: string, size = 18) {
  const paths: Record<string, string> = {
    plus: '<path d="M12 5v14M5 12h14"/>',
    calendar: '<rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18"/>',
    arrow: '<path d="m9 18 6-6-6-6"/>',
    close: '<path d="m6 6 12 12M18 6 6 18"/>',
    external: '<path d="M14 3h7v7M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/>',
    refresh: '<path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 4v5h5M4 13a8.1 8.1 0 0 0 15.5 2M20 20v-5h-5"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    trash: '<path d="M3 6h18M8 6V4h8v2M19 6l-1 15H6L5 6M10 11v6M14 11v6"/>',
    link: '<path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1"/><path d="M14 11a5 5 0 0 0-7.1-.1l-2 2a5 5 0 0 0 7.1 7.1l1.1-1.1"/>',
    phone: '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2.1z"/>',
    search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.4-4.4"/>',
    location: '<path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/>',
    down: '<path d="m6 9 6 6 6-6"/>',
    list: '<path d="M8 6h13M8 12h13M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/>',
    map: '<path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3zM9 3v15M15 6v15"/>',
  };
  return `<svg aria-hidden="true" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${paths[name] || ""}</svg>`;
}

function appError(message: string, code = "REQUEST_FAILED") {
  const error = new Error(message || "Something went wrong.") as Error & { code?: string };
  error.code = code;
  return error;
}

async function rpc<T = any>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw appError(error.message.replace(/^.*P0001:?\s*/i, ""), error.code);
  if ((data as any)?.ok === false) throw appError((data as any).error?.message, (data as any).error?.code);
  return data as T;
}

async function request(path: string, options: { body?: string } = {}): Promise<any> {
  const body = options.body ? JSON.parse(options.body) : {};
  if (path === "/api/maps-preview" || path === "/api/places-search") {
    const { data, error } = await supabase.functions.invoke("maps-preview", { body });
    if (error || data?.error) throw appError(data?.error?.message || error?.message || "Google Places is unavailable.", data?.error?.code);
    return data;
  }
  if (path === "/api/state") return rpc("get_app_state", { p_table_id: body.tableId || null });
  if (path === "/api/default-city") return rpc("set_default_city", { p_table_id: activeTableId(), p_city: body.city });
  if (path === "/api/partnerships") return rpc("create_table", { p_display_name: body.name });
  if (path === "/api/join") return rpc("join_table", { p_token: body.inviteCode, p_display_name: body.name });
  const table = path.match(/^\/api\/tables\/([^/]+)\/delete$/);
  if (table) return rpc("delete_table", { p_table_id: table[1] });
  if (path === "/api/restaurants") return rpc("add_restaurant", { p_table_id: activeTableId(), p_input: body });
  const restaurant = path.match(/^\/api\/restaurants\/([^/]+)$/);
  if (restaurant) return rpc("remove_restaurant", { p_table_id: activeTableId(), p_restaurant_id: restaurant[1] });
  if (path === "/api/plans") return rpc("create_date_plan", { p_table_id: activeTableId(), p_input: body });
  const plan = path.match(/^\/api\/plans\/([^/]+)\/(booking|update-booking|cancel|complete|review|suggest-change)$/);
  if (plan) {
    const [, id, action] = plan;
    const p_table_id = activeTableId();
    if (action === "booking") return rpc("confirm_date_booking", { p_table_id, p_plan_id: id, p_input: body });
    if (action === "update-booking") return rpc("update_date_booking", { p_table_id, p_plan_id: id, p_input: body });
    if (action === "cancel") return rpc("cancel_date_plan", { p_table_id, p_plan_id: id });
    if (action === "complete") return rpc("complete_date_plan", { p_table_id, p_plan_id: id });
    if (action === "review") return rpc("add_date_review", { p_table_id, p_plan_id: id, p_input: body });
    if (action === "suggest-change") return rpc("request_booking_change", { p_table_id, p_plan_id: id, p_input: body });
  }
  throw appError("That action is unavailable.", "NOT_FOUND");
}

async function loadState(silent = false, tableId: string | null = preferredTableId()) {
  if (!session) {
    ui.loading = false;
    render();
    return;
  }
  if (!silent) ui.loading = true;
  try {
    model = await request("/api/state", { body: JSON.stringify({ tableId }) });
    resetBookingSelection();
    if (partnership()) {
      if (inviteCodeFromUrl()) localStorage.setItem(LAST_TABLE_KEY, partnership()!.id);
      else rememberTable(partnership()!.id);
      const defaultCityId = citySlug(partnership()!.defaultCity || "");
      if (!ui.cityId || !partnership()!.cities[ui.cityId]) {
        ui.cityId = partnership()!.cities[defaultCityId]?.id || cities()[0]?.id || null;
      }
    } else if (!inviteCodeFromUrl() && tableIdFromPath()) {
      history.replaceState({}, "", "/");
    }
  } catch (error) {
    const code = (error as Error & { code?: string }).code;
    if (code === "UNAUTHORIZED" || code === "PGRST301") {
      await supabase.auth.signOut();
      model = null;
    } else toast(errorMessage(error), true);
  } finally {
    ui.loading = false;
    render();
  }
}

function toast(message: string, error = false) {
  const node = document.createElement("div");
  node.className = `toast${error ? " error" : ""}`;
  node.textContent = message;
  toastRegion.append(node);
  setTimeout(() => node.remove(), 3800);
}

function render() {
  destroyRestaurantMap();
  if (ui.loading) {
    app.innerHTML = '<main class="loading-page"><span>Setting the table…</span></main>';
    return;
  }
  if (!session) app.innerHTML = authTemplate();
  else if (!model) app.innerHTML = landingTemplate();
  else if (inviteCodeFromUrl()) app.innerHTML = joinInvitationTemplate();
  else if (!partnership()) app.innerHTML = landingTemplate();
  else if (partnership()!.memberIds.length < 2) app.innerHTML = inviteTemplate();
  else {
    app.innerHTML = dashboardTemplate();
    mountBoardMap();
  }
}

function brand() {
  return '<span class="brand-mark">✦</span><span>NextTable</span>';
}

function authTemplate() {
  const codeStep = ui.authStep === "code";
  return `<main class="landing">
    <section class="landing-art"><div class="landing-brand">${brand()}</div><h1>Less deciding.<br>More going.</h1><p>One personal restaurant list. A separate table for every person you make plans with.</p></section>
    <section class="landing-panel"><div class="auth-card">
      <p class="eyebrow">${codeStep ? "Check your inbox" : "Your tables for two"}</p>
      <h2>${codeStep ? "Enter your code." : "Make the next plan."}</h2>
      <p>${codeStep ? `We sent a six-digit code to ${escapeHtml(ui.pendingEmail)}.` : "Sign in with your email. No password to remember."}</p>
      <form class="form-stack" data-form="${codeStep ? "otp" : "email"}">
        ${codeStep
          ? '<div class="field"><label for="otp-code">Six-digit code</label><input id="otp-code" name="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" required placeholder="123456"></div>'
          : '<div class="field"><label for="auth-email">Email</label><input id="auth-email" name="email" type="email" autocomplete="email" required placeholder="you@example.com"></div>'}
        <button class="button primary full" type="submit">${codeStep ? "Open my table" : "Send me a code"} ${icon("arrow")}</button>
      </form>
      ${codeStep ? '<button class="text-button" type="button" data-action="edit-email">Use a different email</button>' : ""}
      <p class="privacy-note">Private by default. Each table’s plans are visible only to its two members.</p>
    </div></section>
  </main>`;
}

function landingTemplate() {
  const invite = inviteCodeFromUrl();
  const savedName = currentUser()?.name || "";
  return `<main class="landing">
    <section class="landing-art"><div class="landing-brand">${brand()}</div><h1>Less deciding.<br>More going.</h1><p>Keep one personal shortlist, then make simple plans with the people at your tables.</p></section>
    <section class="landing-panel"><div class="auth-card">
      <p class="eyebrow">${ui.authMode === "join" ? "Your invitation" : "Start together"}</p>
      <h2>${ui.authMode === "join" ? "Join your person." : "Create a table for two."}</h2>
      <p>${ui.authMode === "join" ? "Open this table and start making plans." : "Create a private table, then invite one person. You can add more tables later."}</p>
      <div class="auth-switch" role="tablist"><button type="button" data-action="auth-mode" data-mode="create" class="${ui.authMode === "create" ? "active" : ""}">Create</button><button type="button" data-action="auth-mode" data-mode="join" class="${ui.authMode === "join" ? "active" : ""}">Join</button></div>
      <form class="form-stack" data-form="table-auth">
        <div class="field"><label for="auth-name">Your first name</label><input id="auth-name" name="name" value="${escapeHtml(savedName)}" autocomplete="given-name" required minlength="2" placeholder="Maya"></div>
        ${ui.authMode === "join" ? `<div class="field"><label for="invite-code">Invitation code</label><input id="invite-code" name="inviteCode" value="${escapeHtml(invite)}" required autocomplete="off"></div>` : ""}
        <button class="button primary full" type="submit">${ui.authMode === "join" ? "Join our table" : "Create our table"} ${icon("arrow")}</button>
      </form>
      <button class="text-button" type="button" data-action="sign-out">Sign out</button>
    </div></section>
  </main>`;
}

function joinInvitationTemplate() {
  const current = currentUser();
  const hasTables = tables().length > 0;
  return `<main class="landing">
    <section class="landing-art"><div class="landing-brand">${brand()}</div><h1>A new table<br>is waiting.</h1><p>Your restaurant shortlist stays yours. Joining only creates a new place for plans with this person.</p></section>
    <section class="landing-panel"><div class="auth-card">
      <p class="eyebrow">Private invitation</p><h2>Join this table?</h2>
      <p>You can switch between every table from the dashboard. Restaurants you save remain available wherever you plan.</p>
      <form class="form-stack" data-form="join-invitation">
        <input type="hidden" name="inviteCode" value="${escapeHtml(inviteCodeFromUrl())}">
        ${current ? `<input type="hidden" name="name" value="">` : '<div class="field"><label for="invite-name">Your first name</label><input id="invite-name" name="name" autocomplete="given-name" required minlength="2" placeholder="Maya"></div>'}
        <button class="button primary full" type="submit">Join table ${icon("arrow")}</button>
      </form>
      <button class="text-button" type="button" data-action="dismiss-invite">${hasTables ? `Back to ${escapeHtml(tableLabel(partnership() || tables()[0]))}` : "Not now"}</button>
    </div></section>
  </main>`;
}

function topbarTemplate() {
  const current = currentUser()!;
  const table = partnership()!;
  const summary = model!.tables[table.id];
  return `<header class="topbar"><button class="brand" type="button" data-action="home">${brand()}</button>
    <button class="table-switcher" type="button" data-action="open-tables" aria-label="Switch table"><span>${escapeHtml(tableLabel(table))}</span>${summary?.needsAction ? '<i aria-label="Action needed"></i>' : ""}${icon("down", 16)}</button>
    <button class="avatar-button" type="button" data-action="open-account" aria-label="Open account">${escapeHtml(initials(current.name))}</button>
  </header>`;
}

function inviteTemplate() {
  const code = partnership()!.inviteCode || "";
  return `<div class="shell">${topbarTemplate()}<main class="invite-page"><section class="invite-card">
    <p class="eyebrow">One seat left</p><h1>Invite your person.</h1>
    <p>Send the private link. It expires after use and can be revoked whenever you need.</p>
    <div class="invite-link-preview"><span>${icon("link")}</span><span><strong>Private invitation ready</strong><code>${escapeHtml(`${location.host}/?invite=••••${code.slice(-8)}`)}</code></span><span class="status-pill">Ready</span></div>
    <div class="button-row"><button class="button primary" type="button" data-action="copy-invite" data-code="${escapeHtml(code)}">Copy invite link</button><button class="button secondary" type="button" data-action="rotate-invite">Replace link</button><button class="button secondary" type="button" data-action="refresh">${icon("refresh")} Check again</button></div>
  </section></main>${ui.modal ? modalTemplate() : ""}</div>`;
}

function dashboardTemplate() {
  const cityList = cities();
  const defaultCityId = citySlug(partnership()!.defaultCity || "");
  const selectedCity = partnership()!.cities[ui.cityId || ""] || partnership()!.cities[defaultCityId] || cityList[0];
  return `<div class="shell">${topbarTemplate()}
    ${cityList.length ? `<nav class="city-tabs" aria-label="Restaurant cities">${cityList.map((city) => `<button class="city-tab ${city.id === selectedCity?.id ? "active" : ""}" type="button" data-action="city" data-city="${escapeHtml(city.id)}">${escapeHtml(city.name)}</button>`).join("")}</nav>` : ""}
    <main class="page">${dateHeroTemplate()}${quickAddTemplate()}${selectedCity ? boardTemplate(selectedCity) : emptyBoardTemplate()}${historyTemplate()}</main>
    ${ui.modal ? modalTemplate() : ""}
  </div>`;
}

function quickAddTemplate() {
  const searchCity = partnership()!.defaultCity?.trim() || "";
  const searchResults = ui.placeSearchLoading
    ? `<div class="search-state"><span class="spinner"></span> ${classifyRestaurantInput(ui.placeQuery) === "google-maps-url" ? "Resolving Google Maps link…" : "Searching Google Places…"}</div>`
    : ui.placeResults.length
      ? `<div class="place-results" role="listbox">${ui.placeResults.map((place, index) => placeResultTemplate(place, index)).join("")}</div>`
      : ui.placeSearchMessage ? `<div class="search-state">${escapeHtml(ui.placeSearchMessage)}</div>` : "";

  return `<section class="quick-add" aria-labelledby="quick-add-title"><div class="quick-add-head"><div><p class="eyebrow">Add a restaurant</p><h2 id="quick-add-title">Find your next place.</h2><p>Search by name or paste a Maps link. Choose the right result, review it, then save to your personal list.</p></div><button class="city-chip ${searchCity ? "" : "needs-city"}" type="button" data-action="edit-search-city" aria-label="${searchCity ? `Change this table’s default city from ${escapeHtml(searchCity)}` : "Set this table’s default city"}">${icon("location", 16)} <span>${escapeHtml(searchCity || "Set default city")}</span></button></div><div class="restaurant-omnibox">${icon("search", 21)}<input id="restaurant-search" value="${escapeHtml(ui.placeQuery)}" autocomplete="off" spellcheck="false" placeholder="Restaurant name or Google Maps link" aria-describedby="restaurant-search-help"></div><span id="restaurant-search-help" class="sr-only">Restaurant name searches use ${escapeHtml(searchCity || "this table’s default city")}. Google Maps links resolve directly.</span><div id="place-search-results" aria-live="polite">${searchResults}</div></section>`;
}

function planRestaurant(plan: DatePlan) {
  return partnership()!.restaurants[plan.restaurantId];
}

function dateHeroTemplate() {
  const { future, overdue } = bookingGroups();
  const pending = myPendingPlan();
  const selected = future.find((plan) => plan.id === ui.selectedPlanId) || future[0];
  const hero = selected ? bookedDateHero(selected, future) : `<section class="date-panel empty-date-panel"><div><p class="eyebrow">Upcoming dates</p><h1>Nothing booked yet.</h1><p>Choose “Plan a date” on one of your restaurants below.</p></div></section>`;
  const pendingReminder = pending ? `<button class="date-reminder private" type="button" data-action="open-plan" data-id="${pending.id}"><span><small>Private plan</small><strong>Finish planning ${escapeHtml(planRestaurant(pending).name)}</strong></span>${icon("arrow")}</button>` : "";
  const overdueReminder = overdue.length ? `<button class="date-reminder overdue" type="button" data-action="open-plan" data-id="${overdue[0].id}"><span><small>${overdue.length === 1 ? "Past date needs an update" : `${overdue.length} past dates need an update`}</small><strong>${escapeHtml(planRestaurant(overdue[0]).name)} · mark it complete or cancel it</strong></span>${icon("arrow")}</button>` : "";
  return `<section class="dates-section">${hero}${pendingReminder || overdueReminder ? `<div class="date-reminders">${pendingReminder}${overdueReminder}</div>` : ""}</section>`;
}

function bookedDateHero(plan: DatePlan, upcoming: DatePlan[]) {
  const restaurant = planRestaurant(plan);
  const bookedByMe = plan.proposerId === model!.currentUserId;
  const organizer = model!.users[plan.proposerId];
  const requester = plan.changeRequestedBy ? model!.users[plan.changeRequestedBy] : null;
  const label = requester && bookedByMe ? "New date suggestion" : bookedByMe ? "You booked this date" : `${escapeHtml(organizer.name)} booked your date`;
  const copy = requester && bookedByMe
    ? `${escapeHtml(requester.name)} suggested ${formatDate(plan.changeRequestedDate!, true)} at ${formatPlanTime(plan, plan.changeRequestedTime, plan.changeRequestedDate)}.`
    : `Reserved for ${formatPlanTime(plan, plan.reservationTime)}.`;
  const position = upcoming.findIndex((item) => item.id === plan.id) + 1;
  const navigation = upcoming.length > 1 ? `<div class="date-panel-nav" aria-label="Browse upcoming dates"><span>${position} of ${upcoming.length}</span><button type="button" data-action="previous-booking" aria-label="Previous upcoming date">‹</button><button type="button" data-action="next-booking" aria-label="Next upcoming date">›</button></div>` : "";
  return `<section class="date-panel"><div><p class="eyebrow">${label}</p><a class="date-restaurant" href="${escapeHtml(restaurant.sourceUrl)}" target="_blank" rel="noreferrer">${escapeHtml(restaurant.name)}</a><p>${escapeHtml(planMeta(plan))} · ${copy}</p></div><div class="date-panel-actions">${navigation}<button class="button light-primary" type="button" data-action="open-plan" data-id="${plan.id}">View details ${icon("arrow")}</button></div></section>`;
}

function filteredRestaurants(city: City) {
  const query = ui.boardSearch.toLowerCase();
  return activeRestaurants().filter((restaurant) => {
    if (restaurant.cityId !== city.id) return false;
    if (ui.filter !== "all" && restaurant.ownerId !== ui.filter) return false;
    const searchable = [restaurant.name, restaurant.neighborhood, restaurant.cuisine, restaurant.address, restaurant.personalNote].join(" ").toLowerCase();
    return searchable.includes(query);
  });
}

function boardTemplate(city: City) {
  const other = partner()!;
  const list = filteredRestaurants(city);
  return `<section class="board" aria-labelledby="board-title"><div class="board-header"><div><p class="eyebrow">${escapeHtml(city.name)}</p><h2 id="board-title">Saved restaurants</h2><p>${mySavedCount()} of 10 saved by you · ${activeRestaurants().length} shared total</p></div><div class="board-tools"><label class="search-box">${icon("search")}<input id="board-search" type="search" value="${escapeHtml(ui.boardSearch)}" placeholder="Search your list"></label></div></div>
    <div class="view-switch" role="group" aria-label="Saved restaurant view"><button class="${ui.boardView === "list" ? "active" : ""}" type="button" data-action="board-view" data-view="list" aria-pressed="${ui.boardView === "list"}">${icon("list", 16)} List</button><button class="${ui.boardView === "map" ? "active" : ""}" type="button" data-action="board-view" data-view="map" aria-pressed="${ui.boardView === "map"}">${icon("map", 16)} Map</button></div>
    <div class="filter-pills"><button class="filter-pill ${ui.filter === "all" ? "active" : ""}" type="button" data-action="filter" data-filter="all">All</button><button class="filter-pill ${ui.filter === model!.currentUserId ? "active" : ""}" type="button" data-action="filter" data-filter="${model!.currentUserId}">Mine</button><button class="filter-pill ${ui.filter === other.id ? "active" : ""}" type="button" data-action="filter" data-filter="${other.id}">${escapeHtml(other.name)}’s</button></div>
    <div data-board-results>${boardResultsTemplate(city, list)}</div>
  </section>`;
}

function boardResultsTemplate(city: City, list: Restaurant[]) {
  if (!list.length) return '<div class="empty-state"><h3>No restaurants here.</h3><p>Try another filter or add a place.</p></div>';
  if (ui.boardView === "list") return `<div class="restaurant-grid">${list.map(restaurantCardTemplate).join("")}</div>`;
  const mapped = list.filter((restaurant) => validCoordinates(restaurant.coordinates));
  const missing = list.length - mapped.length;
  if (ui.mapError) return `<div class="map-recovery"><div class="empty-glyph">⌁</div><h3>The map couldn’t load.</h3><p>Your saved restaurants are still here.</p><div class="button-row"><button class="button secondary" type="button" data-action="retry-map">Try map again</button><button class="button primary" type="button" data-action="show-list">Show List</button></div></div>`;
  if (!mapped.length) return `<div class="map-recovery"><div class="empty-glyph">⌖</div><h3>${list.length} ${list.length === 1 ? "restaurant is" : "restaurants are"} available in List only.</h3><p>They don’t have usable coordinates, so NextTable won’t guess where to put them.</p><button class="button primary" type="button" data-action="show-list">Show List</button></div>`;
  return `<div class="restaurant-map-shell">${missing ? `<div class="map-missing"><strong>${missing} ${missing === 1 ? "restaurant is" : "restaurants are"} in List only</strong><span>Missing map coordinates.</span><button class="text-button" type="button" data-action="show-list">View List</button></div>` : ""}<div id="restaurant-map" class="restaurant-map" role="region" aria-label="Map of ${escapeHtml(city.name)} saved restaurants"></div><div class="map-hint">Drag to explore · Use + and − to zoom · Select a cuisine marker for details</div></div>`;
}

function mountBoardMap() {
  if (ui.boardView !== "map" || ui.mapError) return;
  const currentPartnership = partnership();
  const defaultCityId = citySlug(currentPartnership?.defaultCity || "");
  const city = currentPartnership?.cities[ui.cityId || ""] || currentPartnership?.cities[defaultCityId] || cities()[0];
  const container = document.querySelector<HTMLElement>("#restaurant-map");
  if (!city || !container) return;
  const items: RestaurantMapItem[] = filteredRestaurants(city)
    .filter((restaurant): restaurant is Restaurant & { coordinates: Coordinates } => validCoordinates(restaurant.coordinates))
    .map((restaurant) => ({
      id: restaurant.id,
      name: restaurant.name,
      cuisine: restaurant.cuisine,
      primaryType: restaurant.primaryType,
      neighborhood: restaurant.neighborhood,
      city: restaurant.city,
      mapsUrl: restaurant.googleMapsUrl || restaurant.resolvedUrl || restaurant.sourceUrl,
      ownerName: model!.users[restaurant.ownerId].name,
      coordinates: restaurant.coordinates,
      planActionLabel: restaurant.ownerId === model!.currentUserId ? restaurantPlanActionLabel(restaurant) : undefined,
    }));
  if (!items.length) return;
  mountRestaurantMap(container, items, () => {
    if (ui.boardView !== "map") return;
    ui.mapError = true;
    updateBoardResults();
  }, (restaurantId) => void openRestaurantPlanner(restaurantId).catch((error) => toast(errorMessage(error), true)));
}

function restaurantPlanActionLabel(restaurant: Restaurant) {
  if (!restaurant.openPlan) return "Plan a date";
  if (restaurant.openPlan.stage === "pending_booking") return "Finish plan";
  return `Booked ${formatDate(restaurant.openPlan.proposedDate)}`;
}

function restaurantCardTemplate(restaurant: Restaurant) {
  const owner = model!.users[restaurant.ownerId];
  const rating = restaurant.rating ? `★ ${restaurant.rating}${restaurant.userRatingCount ? ` (${restaurant.userRatingCount.toLocaleString()})` : ""}` : "";
  const mine = restaurant.ownerId === model!.currentUserId;
  return `<article class="restaurant-card"><div class="card-top"><span class="owner-badge"><span class="avatar">${escapeHtml(initials(owner.name))}</span>${escapeHtml(owner.name)}</span>${restaurant.businessStatus && restaurant.businessStatus !== "OPERATIONAL" ? '<span class="status-pill warning">Check status</span>' : ""}</div><div class="card-meta">${escapeHtml([restaurant.neighborhood || restaurant.city, restaurant.cuisine, restaurant.priceRange, rating].filter(Boolean).join(" · "))}</div><h3>${escapeHtml(restaurant.name)}</h3><p>${escapeHtml(restaurant.personalNote || restaurant.address || "Saved for the right day.")}</p>${mine ? `<button class="restaurant-plan-button" type="button" data-action="plan-restaurant" data-id="${restaurant.id}">${icon("calendar", 16)} ${escapeHtml(restaurantPlanActionLabel(restaurant))}<span>${icon("arrow", 16)}</span></button>` : ""}<div class="card-actions"><a class="map-link" href="${escapeHtml(restaurant.sourceUrl)}" target="_blank" rel="noreferrer">Google Maps ${icon("external", 13)}</a>${restaurant.phoneNumber ? `<a class="map-link" href="tel:${escapeHtml(restaurant.phoneNumber)}">${icon("phone", 13)} ${escapeHtml(restaurant.phoneNumber)}</a>` : ""}${mine && !restaurant.openPlan ? `<button class="text-button danger" type="button" data-action="delete-restaurant" data-id="${restaurant.id}">Remove</button>` : ""}</div></article>`;
}

function emptyBoardTemplate() {
  return `<section class="empty-state large"><div class="empty-glyph">✦</div><h1>Your shortlist starts here.</h1><p>Use the search box above to save your first restaurant.</p></section>`;
}

function historyTemplate() {
  const history = completedPlans();
  if (!history.length) return "";
  return `<section class="history"><p class="eyebrow">Already tried</p><h2>Past dates</h2><div class="restaurant-grid">${history.slice(0, 6).map((plan) => {
    const restaurant = planRestaurant(plan);
    const myNote = plan.notes[model!.currentUserId] || plan.reactions[model!.currentUserId] || "Add your review.";
    return `<article class="restaurant-card"><div class="card-meta">${escapeHtml(formatDate(plan.proposedDate))} · ${escapeHtml(capitalize(plan.meal))}</div><h3>${escapeHtml(restaurant.name)}</h3><p>${escapeHtml(myNote)}</p><div class="card-actions"><a class="map-link" href="${escapeHtml(restaurant.sourceUrl)}" target="_blank" rel="noreferrer">Google Maps ${icon("external", 13)}</a><button class="button ghost" type="button" data-action="review" data-id="${plan.id}">Reviews</button></div></article>`;
  }).join("")}</div></section>`;
}

function modalTemplate() {
  if (ui.modal === "review-restaurant") return reviewRestaurantModalTemplate();
  if (ui.modal === "search-city") return searchCityModalTemplate();
  if (ui.modal === "new-plan") return createPlanTemplate();
  if (ui.modal?.startsWith("plan:")) return planModalTemplate(ui.modal.split(":")[1]);
  if (ui.modal === "account") return accountModalTemplate();
  if (ui.modal === "tables") return tablesModalTemplate();
  if (ui.modal?.startsWith("delete-table:")) return deleteTableModalTemplate(ui.modal.split(":")[1]);
  if (ui.modal?.startsWith("suggest-change:")) return suggestBookingChangeTemplate(ui.modal.split(":")[1]);
  if (ui.modal?.startsWith("update-booking:")) return updateBookingTemplate(ui.modal.split(":")[1]);
  if (ui.modal?.startsWith("review:")) return reviewModalTemplate(ui.modal.split(":")[1]);
  return "";
}

function modalFrame(title: string, copy: string, body: string, wide = false) {
  return `<div class="overlay" data-action="overlay-close"><section class="modal ${wide ? "wide" : ""}" role="dialog" aria-modal="true" aria-labelledby="modal-title"><header class="modal-head"><div><h2 id="modal-title">${title}</h2>${copy ? `<p>${copy}</p>` : ""}</div><button class="icon-button" type="button" data-action="close-modal" aria-label="Close">${icon("close")}</button></header><div class="modal-body">${body}</div></section></div>`;
}

function accountModalTemplate() {
  const current = currentUser()!;
  return modalFrame(`${escapeHtml(current.name)}’s account`, `Signed in as ${escapeHtml(session?.user.email || "your account")}.`, `<div class="notice"><strong>${mySavedCount()} of 10 restaurants saved globally</strong><span>Your personal list follows you across all ${tables().length} ${tables().length === 1 ? "table" : "tables"}. Visiting or removing a place frees a slot.</span></div><div class="modal-actions"><button class="text-button danger" type="button" data-action="sign-out">Sign out</button><button class="button secondary" type="button" data-action="close-modal">Done</button></div>`);
}

function tablesModalTemplate() {
  const activeId = partnership()?.id;
  const items = tables().map((table) => {
    const active = table.id === activeId;
    const label = tableLabel(table);
    return `<div class="table-option-row ${active ? "active" : ""}"><button class="table-option" type="button" data-action="switch-table" data-id="${table.id}" ${active ? 'aria-current="page"' : ""}><span class="table-option-avatar">${escapeHtml(initials(tablePartner(table)?.name || "+"))}</span><span><strong>${escapeHtml(label)}</strong><small>${escapeHtml(table.defaultCity || (table.memberIds.length < 2 ? "Invitation ready" : "No default city yet"))}</small></span>${table.needsAction ? '<i>Action needed</i>' : active ? "<em>Current</em>" : icon("arrow", 17)}</button><button class="table-delete" type="button" data-action="confirm-delete-table" data-id="${table.id}" aria-label="Delete ${escapeHtml(label)}" title="Delete table">${icon("trash", 17)}</button></div>`;
  }).join("");
  return modalFrame("Your tables", "Restaurants stay in your personal list. Date plans stay with the person you choose here.", `<div class="table-list">${items}</div><div class="modal-actions split"><button class="text-button" type="button" data-action="close-modal">Cancel</button><button class="button primary" type="button" data-action="create-table">${icon("plus")} Invite someone new</button></div>`);
}

function deleteTableModalTemplate(tableId: string) {
  const table = model!.tables[tableId];
  if (!table) return tablesModalTemplate();
  const other = tablePartner(table);
  const title = other ? `Delete table with ${escapeHtml(other.name)}?` : "Delete this table?";
  const hasBookedDates = tableId === partnership()?.id && plans().some((plan) => plan.stage === "booked");
  return modalFrame(title, "This deletes the shared table for both of you and cannot be undone.", `<div class="notice danger-notice"><strong>${hasBookedDates ? "Cancel every booked date first." : "Your personal restaurants will stay saved."}</strong><span>${hasBookedDates ? "Return to the upcoming dates, cancel each booking, then try deleting this table again." : "Shared plans, invitations, and date history will be permanently removed for both people."}</span></div><div class="modal-actions"><button class="button secondary" type="button" data-action="open-tables">Keep table</button><button class="button danger" type="button" data-action="delete-table" data-id="${table.id}">${icon("trash")} Delete table</button></div>`);
}

function reviewRestaurantModalTemplate() {
  if (!ui.selectedPlace) return "";
  return modalFrame("Review the restaurant", "Google filled in what it could. Correct anything that looks wrong, then save.", `<form class="form-stack" data-form="restaurant">${restaurantDetailsTemplate(ui.selectedPlace)}<div class="modal-actions"><button class="button secondary" type="button" data-action="clear-place">Back to results</button><button class="button primary" type="submit">Save restaurant ${icon("arrow")}</button></div></form>`, true);
}

function searchCityModalTemplate() {
  const city = partnership()!.defaultCity || partnership()!.cities[ui.cityId || ""]?.name || "";
  return modalFrame("Set this table’s default city", "Name searches at this table start here. You can still save and browse restaurants in any city.", `<form class="form-stack" data-form="default-city"><div class="field"><label for="default-city">City</label><input id="default-city" name="city" value="${escapeHtml(city)}" autocomplete="address-level2" minlength="2" maxlength="120" required placeholder="San Francisco"></div><div class="modal-actions"><button class="button secondary" type="button" data-action="close-modal">Cancel</button><button class="button primary" type="submit">Save default</button></div></form>`);
}

function placeResultTemplate(place: PlaceResult, index: number) {
  const meta = [place.neighborhood || place.city, place.cuisine, place.priceRange, place.rating ? `★ ${place.rating}` : ""].filter(Boolean).join(" · ");
  return `<button class="place-result" type="button" data-action="select-place" data-index="${index}" role="option"><span><strong>${escapeHtml(place.name)}</strong><small>${escapeHtml(place.address || meta)}</small>${place.address && meta ? `<small>${escapeHtml(meta)}</small>` : ""}</span><span class="choose-label">Choose ${icon("arrow", 14)}</span></button>`;
}

function restaurantDetailsTemplate(place: PlaceResult) {
  return `<fieldset class="restaurant-details"><legend>Review the details</legend><div class="selected-place-summary"><span class="selected-check">${icon("check")}</span><div><strong>${escapeHtml(place.name)}</strong><p>${escapeHtml(place.address || place.city || "Google place selected")}</p></div><button class="text-button" type="button" data-action="clear-place">Change</button></div>
    <div class="field-row"><div class="field"><label for="restaurant-name">Name</label><input id="restaurant-name" name="name" value="${escapeHtml(place.name)}" required></div><div class="field"><label for="restaurant-city">City</label><input id="restaurant-city" name="city" value="${escapeHtml(place.city || partnership()!.defaultCity)}" required></div></div>
    <div class="field"><label for="restaurant-address">Address <span>Optional</span></label><input id="restaurant-address" name="address" value="${escapeHtml(place.address)}"></div>
    <div class="field-row"><div class="field"><label for="restaurant-neighborhood">Neighborhood <span>Optional</span></label><input id="restaurant-neighborhood" name="neighborhood" value="${escapeHtml(place.neighborhood)}"></div><div class="field"><label for="restaurant-cuisine">Cuisine <span>Optional</span></label><input id="restaurant-cuisine" name="cuisine" value="${escapeHtml(place.cuisine)}"></div></div>
    <div class="field-row"><div class="field"><label for="restaurant-price">Price <span>Optional</span></label><select id="restaurant-price" name="priceRange"><option value="">Unknown</option>${["$", "$$", "$$$", "$$$$"].map((price) => `<option ${place.priceRange === price ? "selected" : ""}>${price}</option>`).join("")}</select></div><div class="field"><label for="restaurant-phone">Phone <span>Optional</span></label><input id="restaurant-phone" name="phoneNumber" value="${escapeHtml(place.phoneNumber)}"></div></div>
    <div class="field"><label for="restaurant-website">Website <span>Optional</span></label><input id="restaurant-website" name="websiteUrl" type="url" value="${escapeHtml(place.websiteUrl)}"></div>
    <div class="field"><label for="restaurant-note">Why save it? <span>Optional</span></label><textarea id="restaurant-note" name="personalNote" placeholder="Great patio, handmade pasta…"></textarea></div>
  </fieldset>`;
}

function planModalTemplate(planId: string) {
  const plan = partnership()!.plans[planId];
  if (!plan) return "";
  if (plan.stage === "pending_booking") return bookingTemplate(plan);
  if (plan.stage === "booked") return bookedTemplate(plan);
  return "";
}

function createPlanTemplate() {
  const restaurant = ui.selectedRestaurantId ? partnership()!.restaurants[ui.selectedRestaurantId] : null;
  if (!restaurant || restaurant.ownerId !== model!.currentUserId || restaurant.status !== "active") return "";
  const selectedTimeZone = restaurant.timeZone;
  return modalFrame("Plan a date", `Choose a day for ${escapeHtml(restaurant.name)}. This stays private until you record a confirmed reservation for ${escapeHtml(partner()!.name)}.`, `<form class="form-stack" data-form="create-plan"><div class="plan-restaurant"><p class="eyebrow">Your restaurant</p><a href="${escapeHtml(restaurant.sourceUrl)}" target="_blank" rel="noreferrer">${escapeHtml(restaurant.name)} ${icon("external", 16)}</a><p>${escapeHtml([restaurant.neighborhood || restaurant.city, restaurant.cuisine].filter(Boolean).join(" · "))}</p></div><div class="field-row"><div class="field"><label for="plan-date">Date</label><input id="plan-date" name="date" type="date" min="${today(selectedTimeZone)}" value="${defaultDate(selectedTimeZone)}" required></div><div class="field"><label for="plan-meal">Meal</label><select id="plan-meal" name="meal" required><option value="dinner">Dinner</option><option value="lunch">Lunch</option></select></div></div><fieldset class="optional-time"><legend>Optional time range</legend><p>Times use the restaurant’s local timezone${selectedTimeZone ? ` (${escapeHtml(selectedTimeZone)})` : ""}.</p><div class="field-row"><div class="field"><label for="time-start">Earliest</label><input id="time-start" name="timeStart" type="time"></div><div class="field"><label for="time-end">Latest</label><input id="time-end" name="timeEnd" type="time"></div></div></fieldset><div class="notice"><strong>Your partner will not see this yet.</strong><span>The plan appears for both of you only after you enter the time you actually booked.</span></div><div class="modal-actions"><button class="button secondary" type="button" data-action="close-modal">Not yet</button><button class="button primary" type="submit">Continue to booking ${icon("arrow")}</button></div></form>`);
}

function bookingTemplate(plan: DatePlan) {
  const restaurant = planRestaurant(plan);
  const suggestedTime = plan.timeStart ? plan.timeStart.slice(0, 5) : plan.meal === "lunch" ? "12:30" : "19:00";
  return modalFrame("Reserve the table", `${escapeHtml(restaurant.name)} · ${escapeHtml(planMeta(plan))}`, `${restaurantContactTemplate(restaurant)}<div class="booking-steps"><p class="eyebrow">Private until booked</p><h3>Call or book online, then record the reservation.</h3><p>${escapeHtml(model!.users[plan.partnerId].name)} will see the date only after you save the confirmed time.</p></div><form class="form-stack" data-form="booking" data-id="${plan.id}"><div class="field-row"><div class="field"><label for="reservation-time">Confirmed time</label><input id="reservation-time" name="reservationTime" type="time" value="${escapeHtml(suggestedTime)}" required></div><div class="field"><label for="booking-note">Confirmation note <span>Optional</span></label><input id="booking-note" name="bookingNote" maxlength="500" placeholder="Booked by phone, patio requested"></div></div><div class="modal-actions split"><button class="text-button danger" type="button" data-action="cancel-plan" data-id="${plan.id}">Cancel plan</button><button class="button primary" type="submit">Share booked date ${icon("check")}</button></div></form>`);
}

function bookedTemplate(plan: DatePlan) {
  const restaurant = planRestaurant(plan);
  const bookedByMe = plan.proposerId === model!.currentUserId;
  const organizer = model!.users[plan.proposerId];
  const requester = plan.changeRequestedBy ? model!.users[plan.changeRequestedBy] : null;
  const changeRequest = requester && bookedByMe
    ? `<div class="notice"><strong>${escapeHtml(requester.name)} suggested a different date</strong><span>${formatDate(plan.changeRequestedDate!, true)} at ${formatPlanTime(plan, plan.changeRequestedTime, plan.changeRequestedDate)}${plan.changeRequestNote ? ` · ${escapeHtml(plan.changeRequestNote)}` : ""}. This has not changed the reservation. Update the confirmed booking or cancel the date to resolve it.</span><button class="button secondary" type="button" data-action="update-booking" data-id="${plan.id}">Update booking ${icon("arrow")}</button></div>`
    : !bookedByMe && plan.changeRequestedBy === model!.currentUserId
      ? `<div class="notice"><strong>Your suggestion was sent to ${escapeHtml(organizer.name)}</strong><span>${formatDate(plan.changeRequestedDate!, true)} at ${formatPlanTime(plan, plan.changeRequestedTime, plan.changeRequestedDate)}. The current reservation is still confirmed.</span></div>`
      : "";
  const secondaryAction = bookedByMe
    ? `<button class="text-button danger" type="button" data-action="cancel-plan" data-id="${plan.id}">Cancel date</button>`
    : `<button class="text-button" type="button" data-action="suggest-change" data-id="${plan.id}">${plan.changeRequestedBy === model!.currentUserId ? "Update suggestion" : "Suggest a different date/time"}</button>`;
  return modalFrame(bookedByMe ? "You booked this date" : `${escapeHtml(organizer.name)} booked your date`, `${formatDate(plan.proposedDate, true)} at ${formatPlanTime(plan, plan.reservationTime)}.`, `<div class="booking-confirmed"><span>${icon("check", 28)}</span><div><p class="eyebrow">${bookedByMe ? "Reservation confirmed" : `Booked by ${escapeHtml(organizer.name)}`}</p><a href="${escapeHtml(restaurant.sourceUrl)}" target="_blank" rel="noreferrer">${escapeHtml(restaurant.name)} ${icon("external", 16)}</a><p>${escapeHtml(restaurant.address || restaurant.neighborhood || restaurant.city)}</p>${plan.bookingNote ? `<small>${escapeHtml(plan.bookingNote)}</small>` : ""}</div></div>${changeRequest}${restaurantContactTemplate(restaurant)}<div class="modal-actions split">${secondaryAction}<div class="button-row"><button class="button secondary" type="button" data-action="calendar" data-id="${plan.id}">${icon("calendar")} Add to calendar</button>${plan.changeRequestedBy ? "" : `<button class="button primary" type="button" data-action="complete-plan" data-id="${plan.id}">We went ${icon("arrow")}</button>`}</div></div>`);
}

function suggestBookingChangeTemplate(planId: string) {
  const plan = partnership()!.plans[planId];
  const organizer = model!.users[plan.proposerId];
  return modalFrame("Suggest a different date/time", `Times use the restaurant’s local timezone${plan.timeZone ? ` (${escapeHtml(plan.timeZone)})` : ""}. This does not cancel or change the current reservation.`, `<form class="form-stack" data-form="suggest-change" data-id="${plan.id}"><div class="field-row"><div class="field"><label for="change-date">Preferred date</label><input id="change-date" name="date" type="date" min="${today(plan.timeZone)}" value="${escapeHtml(plan.changeRequestedDate || plan.proposedDate)}" required></div><div class="field"><label for="change-time">Preferred time</label><input id="change-time" name="time" type="time" value="${escapeHtml((plan.changeRequestedTime || plan.reservationTime || "19:00").slice(0, 5))}" required></div></div><div class="field"><label for="change-note">Why? <span>Optional</span></label><input id="change-note" name="note" maxlength="500" value="${escapeHtml(plan.changeRequestNote || "")}" placeholder="I have a conflict before 7:30"></div><div class="modal-actions"><button class="button secondary" type="button" data-action="open-plan" data-id="${plan.id}">Back</button><button class="button primary" type="submit">Notify ${escapeHtml(organizer.name)} ${icon("arrow")}</button></div></form>`);
}

function updateBookingTemplate(planId: string) {
  const plan = partnership()!.plans[planId];
  const requester = plan.changeRequestedBy ? model!.users[plan.changeRequestedBy] : null;
  const restaurant = planRestaurant(plan);
  if (!requester || plan.proposerId !== model!.currentUserId) return bookedTemplate(plan);
  return modalFrame("Update the booking", `${escapeHtml(requester.name)} suggested ${formatDate(plan.changeRequestedDate!, true)} at ${formatPlanTime(plan, plan.changeRequestedTime, plan.changeRequestedDate)}. Confirm the revised reservation before saving it.`, `${restaurantContactTemplate(restaurant)}<form class="form-stack" data-form="update-booking" data-id="${plan.id}"><div class="field-row"><div class="field"><label for="updated-date">Confirmed date</label><input id="updated-date" name="date" type="date" min="${today(plan.timeZone)}" value="${escapeHtml(plan.changeRequestedDate)}" required></div><div class="field"><label for="updated-time">Confirmed time</label><input id="updated-time" name="reservationTime" type="time" value="${escapeHtml(plan.changeRequestedTime?.slice(0, 5))}" required></div></div><div class="field"><label for="updated-booking-note">Confirmation note <span>Optional</span></label><input id="updated-booking-note" name="bookingNote" maxlength="500" value="${escapeHtml(plan.bookingNote)}" placeholder="Rebooked by phone"></div><div class="notice"><strong>This resolves the pending suggestion.</strong><span>Both of you will receive an updated calendar event using the original event ID and ${escapeHtml(plan.timeZone || "the restaurant’s local timezone")}.</span></div><div class="modal-actions split"><button class="text-button danger" type="button" data-action="cancel-plan" data-id="${plan.id}">Cancel date</button><div class="button-row"><button class="button secondary" type="button" data-action="open-plan" data-id="${plan.id}">Back</button><button class="button primary" type="submit">Save revised booking ${icon("check")}</button></div></div></form>`);
}

function restaurantContactTemplate(restaurant: Restaurant) {
  return `<div class="contact-row">${restaurant.phoneNumber ? `<a class="contact-action" href="tel:${escapeHtml(restaurant.phoneNumber)}">${icon("phone")}<span><strong>Call restaurant</strong><small>${escapeHtml(restaurant.phoneNumber)}</small></span></a>` : '<div class="contact-action unavailable"><span>Phone not available</span></div>'}${restaurant.websiteUrl ? `<a class="contact-action" href="${escapeHtml(restaurant.websiteUrl)}" target="_blank" rel="noreferrer">${icon("external")}<span><strong>Restaurant website</strong><small>Open booking options</small></span></a>` : ""}<a class="contact-action" href="${escapeHtml(restaurant.sourceUrl)}" target="_blank" rel="noreferrer">${icon("external")}<span><strong>Google Maps</strong><small>Hours, photos, and directions</small></span></a></div>`;
}

function reviewModalTemplate(planId: string) {
  const plan = partnership()!.plans[planId];
  const restaurant = planRestaurant(plan);
  const current = currentUser()!;
  const other = partner()!;
  const reaction = plan.reactions[current.id] || "";
  const partnerReaction = plan.reactions[other.id] || "";
  const partnerNote = plan.notes[other.id] || "";
  return modalFrame("How was it?", `${escapeHtml(restaurant.name)} · ${formatDate(plan.proposedDate, true)}. Reviews are optional and separate.`, `<div class="review-stack"><form class="review-card" data-form="review" data-id="${plan.id}"><strong>${escapeHtml(current.name)}’s review</strong><div class="reaction-row">${["Not for us", "Good", "Loved it"].map((item) => `<button class="reaction ${reaction === item ? "active" : ""}" type="button" data-action="reaction" data-value="${item}">${item}</button>`).join("")}</div><input type="hidden" id="review-reaction" name="reaction" value="${escapeHtml(reaction)}"><div class="field"><label for="review-note">Your notes <span>Optional</span></label><textarea id="review-note" name="note" maxlength="2000">${escapeHtml(plan.notes[current.id] || "")}</textarea></div><div class="modal-actions"><button class="button secondary" type="button" data-action="close-modal">Later</button><button class="button primary" type="submit">Save review</button></div></form><section class="review-card muted"><strong>${escapeHtml(other.name)}’s review</strong>${partnerReaction || partnerNote ? `${partnerReaction ? `<span class="review-chip">${escapeHtml(partnerReaction)}</span>` : ""}${partnerNote ? `<p>${escapeHtml(partnerNote)}</p>` : ""}` : '<p>Nothing added yet.</p>'}</section></div>`);
}

function planMeta(plan: DatePlan) {
  const range = plan.timeStart && plan.timeEnd ? ` · ${formatPlanTime(plan, plan.timeStart)}–${formatPlanTime(plan, plan.timeEnd)}` : "";
  return `${formatDate(plan.proposedDate, true)} · ${capitalize(plan.meal)}${range}`;
}

function capitalize(value: string) { return value.charAt(0).toUpperCase() + value.slice(1); }
function formatDate(value: string, long = false) {
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat("en-US", long ? { weekday: "long", month: "long", day: "numeric" } : { month: "short", day: "numeric" }).format(date);
}
function formatTime(value?: string | null, timeZone?: string | null, date = "2000-01-01") {
  if (!value) return "time TBD";
  if (timeZone) {
    return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone, timeZoneName: "short" })
      .format(zonedDateTimeToUtc(date, value, timeZone));
  }
  const [hours, minutes] = value.split(":").map(Number);
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(new Date(2000, 0, 1, hours, minutes));
}
function formatPlanTime(plan: DatePlan, value?: string | null, date: string | null = plan.proposedDate) { return formatTime(value, plan.timeZone, date || plan.proposedDate); }
function dateInTimeZone(value: Date, timeZone?: string | null) {
  if (!timeZone) return value.toISOString().slice(0, 10);
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(value).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}
function today(timeZone?: string | null) { return dateInTimeZone(new Date(), timeZone); }
function defaultDate(timeZone?: string | null) { const date = new Date(); date.setUTCDate(date.getUTCDate() + 7); return dateInTimeZone(date, timeZone); }
function formObject(form: HTMLFormElement) { return Object.fromEntries(new FormData(form).entries()); }

async function openPlan(planId: string) {
  const plan = partnership()!.plans[planId];
  if (!plan) throw new Error("That date is no longer available.");
  if (!plan.timeZone) await ensurePlanTimeZone(plan);
  if (plan.stage === "booked") ui.selectedPlanId = plan.id;
  ui.selectedRestaurantId = null;
  ui.modal = `plan:${plan.id}`;
  render();
}

async function openRestaurantPlanner(restaurantId: string) {
  let restaurant = partnership()!.restaurants[restaurantId];
  if (!restaurant || restaurant.ownerId !== model!.currentUserId || restaurant.status !== "active") {
    throw new Error("You can only plan with one of your own saved restaurants.");
  }

  if (restaurant.openPlan) {
    const { tableId, id: planId } = restaurant.openPlan;
    if (tableId !== activeTableId()) await switchTable(tableId);
    await openPlan(planId);
    return;
  }

  const pending = myPendingPlan();
  if (pending && pending.restaurantId !== restaurant.id) {
    if (!confirm(`You already have a private plan at ${planRestaurant(pending).name} for this table. Cancel it and plan at ${restaurant.name} instead?`)) return;
    const result = await request(`/api/plans/${pending.id}/cancel`, { body: "{}" });
    model = result.state;
    resetBookingSelection();
    restaurant = partnership()!.restaurants[restaurantId];
  }

  await restaurantTimeZone(restaurant);
  ui.selectedRestaurantId = restaurant.id;
  ui.modal = "new-plan";
  render();
}

async function runPlaceSearch() {
  const sequence = ++placeSearchSequence;
  const searchCity = partnership()?.defaultCity?.trim();
  if (ui.placeQuery.trim().length < 2 || !searchCity) return;
  ui.placeSearchLoading = true;
  ui.placeSearchMessage = "";
  updatePlaceResults();
  try {
    const response = await request("/api/places-search", { body: JSON.stringify({ query: ui.placeQuery.trim(), city: searchCity }) });
    if (sequence !== placeSearchSequence) return;
    ui.placeResults = response.results || [];
    ui.placeSearchMessage = ui.placeResults.length ? "" : "No likely matches. Keep typing or try the full name.";
  } catch (error) {
    if (sequence !== placeSearchSequence) return;
    ui.placeResults = [];
    ui.placeSearchMessage = errorMessage(error);
  } finally {
    if (sequence === placeSearchSequence) {
      ui.placeSearchLoading = false;
      updatePlaceResults();
    }
  }
}

function updatePlaceResults() {
  const region = document.querySelector<HTMLElement>("#place-search-results");
  if (!region) return;
  region.innerHTML = ui.placeSearchLoading
    ? '<div class="search-state"><span class="spinner"></span> Searching Google Places…</div>'
    : ui.placeResults.length
      ? `<div class="place-results" role="listbox">${ui.placeResults.map((place, index) => placeResultTemplate(place, index)).join("")}</div>`
      : ui.placeSearchMessage ? `<div class="search-state">${escapeHtml(ui.placeSearchMessage)}</div>` : "";
}

async function resolveRestaurantUrl(sourceUrl: string) {
  const sequence = ++placeSearchSequence;
  ui.placeSearchLoading = true;
  ui.placeResults = [];
  ui.placeSearchMessage = "";
  updatePlaceResults();
  try {
    const selected = await request("/api/maps-preview", { body: JSON.stringify({ sourceUrl }) });
    if (sequence !== placeSearchSequence) return;
    if (!selected?.name) throw new Error("Google did not identify a restaurant from that link. Try searching by name.");
    ui.selectedPlace = selected;
    ui.placeSearchLoading = false;
    ui.modal = "review-restaurant";
    render();
  } catch (error) {
    if (sequence !== placeSearchSequence) return;
    ui.placeSearchLoading = false;
    ui.placeSearchMessage = errorMessage(error);
    updatePlaceResults();
  }
}

async function selectPlace(index: number) {
  const candidate = ui.placeResults[index];
  if (!candidate?.googlePlaceId) throw new Error("Google did not return a place identifier. Try another result.");
  const sequence = ++placeSearchSequence;
  ui.placeSearchLoading = true;
  ui.placeSearchMessage = "Loading phone, website, hours, and rating…";
  updatePlaceResults();
  try {
    const enriched = await request("/api/maps-preview", { body: JSON.stringify({ placeId: candidate.googlePlaceId, city: partnership()?.defaultCity }) });
    if (sequence !== placeSearchSequence) return;
    const selected = { ...candidate, ...enriched } as PlaceResult;
    ui.selectedPlace = selected;
    ui.placeSearchLoading = false;
    ui.modal = "review-restaurant";
    render();
  } catch (error) {
    if (sequence !== placeSearchSequence) return;
    ui.placeSearchLoading = false;
    ui.placeSearchMessage = errorMessage(error);
    updatePlaceResults();
    throw error;
  }
}

async function handleRestaurant(form: HTMLFormElement) {
  if (!ui.selectedPlace) throw new Error("Choose a Google place first.");
  const payload = { ...ui.selectedPlace, ...formObject(form) };
  const result = await request("/api/restaurants", { body: JSON.stringify(payload) });
  model = result.state;
  ui.cityId = result.restaurant.cityId;
  ui.modal = null;
  ui.filter = "all";
  ui.placeQuery = "";
  ui.placeResults = [];
  ui.placeSearchMessage = "";
  ui.selectedPlace = null;
  render();
  toast(`${result.restaurant.name} saved. You have ${mySavedCount()} of 10.`);
}

async function handleDefaultCity(form: HTMLFormElement) {
  const city = String(formObject(form).city || "").trim();
  const result = await request("/api/default-city", { body: JSON.stringify({ city }) });
  model = result.state;
  ui.cityId = citySlug(city);
  ui.modal = null;
  ui.placeResults = [];
  ui.placeSearchMessage = "";
  render();
  focusRestaurantInput();
}

async function restaurantTimeZone(restaurant: Restaurant) {
  if (restaurant.timeZone) return restaurant.timeZone;
  const body = restaurant.googlePlaceId
    ? { placeId: restaurant.googlePlaceId, city: restaurant.city }
    : { sourceUrl: restaurant.sourceUrl };
  const resolved = await request("/api/maps-preview", { body: JSON.stringify(body) }) as PlaceResult;
  if (!resolved.timeZone) throw new Error("Could not determine the restaurant’s timezone.");
  restaurant.timeZone = resolved.timeZone;
  return resolved.timeZone;
}

async function ensurePlanTimeZone(plan: DatePlan) {
  if (plan.timeZone) return plan.timeZone;
  const timeZone = await restaurantTimeZone(planRestaurant(plan));
  const result = await rpc<any>("set_date_plan_timezone", {
    p_table_id: activeTableId(),
    p_plan_id: plan.id,
    p_time_zone: timeZone,
  });
  model = result.state;
  return timeZone;
}

async function selectCity(cityId: string) {
  ui.cityId = cityId;
  ui.filter = "all";
  ui.mapError = false;
  ui.boardSearch = "";
  render();
}

function resetTableUi() {
  ui.modal = null;
  ui.cityId = null;
  ui.filter = "all";
  ui.mapError = false;
  ui.boardSearch = "";
  ui.placeQuery = "";
  ui.placeResults = [];
  ui.placeSearchMessage = "";
  ui.selectedPlace = null;
  ui.selectedRestaurantId = null;
  ui.selectedPlanId = null;
}

async function switchTable(tableId: string, push = true) {
  if (tableId === partnership()?.id) {
    ui.modal = null;
    render();
    return;
  }
  resetTableUi();
  if (push) history.pushState({}, "", `/t/${tableId}`);
  await loadState(false, tableId);
}

async function createAnotherTable() {
  const result = await request("/api/partnerships", { body: JSON.stringify({ name: "" }) });
  model = result.state;
  resetTableUi();
  rememberTable(result.tableId, false);
  render();
  toast("New table ready. Send the invitation when you’re ready.");
}

async function deleteSharedTable(tableId: string) {
  const currentId = partnership()?.id || null;
  const result = await request(`/api/tables/${tableId}/delete`);
  const fallbackId = currentId && currentId !== tableId ? currentId : result.nextTableId || null;
  resetTableUi();
  if (!fallbackId) localStorage.removeItem(LAST_TABLE_KEY);
  await loadState(false, fallbackId);
  toast("Table deleted. Your personal restaurants are still saved.");
}

function dismissInvitation() {
  if (partnership()) rememberTable(partnership()!.id);
  else history.replaceState({}, "", "/");
  ui.authMode = "create";
  render();
}

function focusRestaurantInput() {
  requestAnimationFrame(() => {
    document.querySelector<HTMLInputElement>("#restaurant-search")?.focus();
    document.querySelector(".quick-add")?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

async function handleCreatePlan(form: HTMLFormElement) {
  if (!ui.selectedRestaurantId) throw new Error("Choose a restaurant.");
  const restaurant = partnership()!.restaurants[ui.selectedRestaurantId];
  const timeZone = await restaurantTimeZone(restaurant);
  const payload = { ...formObject(form), restaurantId: ui.selectedRestaurantId, timeZone };
  const result = await request("/api/plans", { body: JSON.stringify(payload) });
  model = result.state;
  resetBookingSelection();
  ui.selectedRestaurantId = null;
  ui.modal = `plan:${result.plan.id}`;
  render();
  toast("Plan ready. Reserve it before your partner sees it.");
}

async function handleBooking(form: HTMLFormElement) {
  const plan = partnership()!.plans[form.dataset.id!];
  const timeZone = plan.timeZone || await restaurantTimeZone(planRestaurant(plan));
  const result = await request(`/api/plans/${form.dataset.id}/booking`, { body: JSON.stringify({ ...formObject(form), timeZone }) });
  model = result.state;
  resetBookingSelection();
  ui.modal = `plan:${form.dataset.id}`;
  render();
  toast("Reservation saved.");
}

async function handleUpdatedBooking(form: HTMLFormElement) {
  const plan = partnership()!.plans[form.dataset.id!];
  const timeZone = plan.timeZone || await restaurantTimeZone(planRestaurant(plan));
  const result = await request(`/api/plans/${form.dataset.id}/update-booking`, { body: JSON.stringify({ ...formObject(form), timeZone }) });
  model = result.state;
  resetBookingSelection();
  ui.modal = `plan:${form.dataset.id}`;
  render();
  toast("Booking updated. The suggestion is resolved.");
}

async function handleBookingChange(form: HTMLFormElement) {
  const plan = partnership()!.plans[form.dataset.id!];
  const timeZone = plan.timeZone || await restaurantTimeZone(planRestaurant(plan));
  const result = await request(`/api/plans/${form.dataset.id}/suggest-change`, { body: JSON.stringify({ ...formObject(form), timeZone }) });
  model = result.state;
  ui.modal = `plan:${form.dataset.id}`;
  render();
  toast(`Suggestion sent to ${model!.users[partnership()!.plans[form.dataset.id!].proposerId].name}.`);
}

async function cancelPlan(planId: string) {
  if (!confirm("Cancel this date plan? The restaurant will stay in your saved list.")) return;
  const plan = partnership()!.plans[planId];
  if (plan.stage === "booked" && !plan.timeZone) await ensurePlanTimeZone(plan);
  const result = await request(`/api/plans/${planId}/cancel`, { body: "{}" });
  model = result.state;
  resetBookingSelection();
  ui.modal = null;
  render();
  toast("Date plan canceled.");
}

async function completePlan(planId: string) {
  const result = await request(`/api/plans/${planId}/complete`, { body: "{}" });
  model = result.state;
  resetBookingSelection();
  ui.modal = `review:${planId}`;
  render();
  toast("Date completed. That restaurant no longer counts toward the saved limit.");
}

async function handleReview(form: HTMLFormElement) {
  const result = await request(`/api/plans/${form.dataset.id}/review`, { body: JSON.stringify(formObject(form)) });
  model = result.state;
  ui.modal = null;
  render();
  toast("Review saved.");
}

async function deleteRestaurant(id: string) {
  const restaurant = partnership()!.restaurants[id];
  if (!confirm(`Remove ${restaurant.name} from your saved list?`)) return;
  const result = await request(`/api/restaurants/${id}`, { body: "{}" });
  model = result.state;
  render();
  toast(`${restaurant.name} removed.`);
}

function updateBoardResults() {
  const city = partnership()?.cities[ui.cityId || ""];
  const region = document.querySelector<HTMLElement>("[data-board-results]");
  if (!city || !region) return render();
  const list = filteredRestaurants(city);
  destroyRestaurantMap();
  region.innerHTML = boardResultsTemplate(city, list);
  document.querySelectorAll<HTMLElement>('[data-action="filter"]').forEach((button) => button.classList.toggle("active", button.dataset.filter === ui.filter));
  mountBoardMap();
}

async function downloadCalendar(planId: string) {
  let plan = partnership()!.plans[planId];
  if (!plan.timeZone) {
    await ensurePlanTimeZone(plan);
    plan = partnership()!.plans[planId];
  }
  const restaurant = planRestaurant(plan);
  const sequence = await rpc<number>("get_date_calendar_sequence", { p_table_id: activeTableId(), p_plan_id: planId });
  const ics = buildCalendarEvent({
    uid: plan.id,
    sequence,
    date: plan.proposedDate,
    time: plan.reservationTime || "19:00",
    timeZone: plan.timeZone!,
    summary: `${capitalize(plan.meal)} at ${restaurant.name}`,
    location: restaurant.address || restaurant.city,
    description: `Planned in NextTable. ${restaurant.sourceUrl}`,
  });
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${restaurant.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${plan.proposedDate}.ics`;
  link.click();
  URL.revokeObjectURL(url);
}

app.addEventListener("click", async (event) => {
  const control = (event.target as Element).closest<HTMLElement>("[data-action]");
  if (!control) return;
  const action = control.dataset.action;
  try {
    if (action === "auth-mode") { ui.authMode = control.dataset.mode as "create" | "join"; render(); }
    if (action === "edit-email") { ui.authStep = "email"; ui.pendingEmail = ""; render(); }
    if (action === "copy-invite") { await navigator.clipboard.writeText(`${location.origin}/?invite=${encodeURIComponent(control.dataset.code || "")}`); toast("Invitation link copied."); }
    if (action === "refresh") await loadState(true);
    if (action === "sign-out") { await supabase.auth.signOut(); session = null; model = null; ui.modal = null; history.replaceState({}, "", "/"); render(); }
    if (action === "rotate-invite") { if (confirm("Replace the current invitation link?")) { const result = await rpc<any>("rotate_invitation", { p_table_id: activeTableId() }); model = result.state; render(); toast("New invitation ready."); } }
    if (action === "home") { ui.modal = null; render(); window.scrollTo({ top: 0, behavior: "smooth" }); }
    if (action === "open-tables") { ui.modal = "tables"; render(); }
    if (action === "switch-table" && control.dataset.id) await switchTable(control.dataset.id);
    if (action === "confirm-delete-table" && control.dataset.id) { ui.modal = `delete-table:${control.dataset.id}`; render(); }
    if (action === "delete-table" && control.dataset.id) await deleteSharedTable(control.dataset.id);
    if (action === "create-table") await createAnotherTable();
    if (action === "dismiss-invite") dismissInvitation();
    if (action === "city" && control.dataset.city) await selectCity(control.dataset.city);
    if (action === "board-view") { ui.boardView = control.dataset.view === "map" ? "map" : "list"; ui.mapError = false; localStorage.setItem(BOARD_VIEW_KEY, ui.boardView); render(); }
    if (action === "show-list") { ui.boardView = "list"; ui.mapError = false; localStorage.setItem(BOARD_VIEW_KEY, "list"); render(); }
    if (action === "retry-map") { ui.mapError = false; updateBoardResults(); }
    if (action === "filter") { ui.filter = control.dataset.filter || "all"; ui.mapError = false; updateBoardResults(); }
    if (action === "edit-search-city") { ui.modal = "search-city"; render(); }
    if (action === "focus-add") { ui.modal = null; render(); focusRestaurantInput(); }
    if (action === "open-plan" && control.dataset.id) await openPlan(control.dataset.id);
    if (action === "plan-restaurant" && control.dataset.id) await openRestaurantPlanner(control.dataset.id);
    if (action === "previous-booking" || action === "next-booking") {
      const future = bookingGroups().future;
      const currentIndex = Math.max(0, future.findIndex((plan) => plan.id === ui.selectedPlanId));
      const offset = action === "previous-booking" ? -1 : 1;
      ui.selectedPlanId = future[(currentIndex + offset + future.length) % future.length]?.id || null;
      render();
    }
    if (action === "open-account") { ui.modal = "account"; render(); }
    if (action === "close-modal") { ui.modal = null; ui.selectedPlace = null; render(); }
    if (action === "overlay-close" && event.target === control) { ui.modal = null; ui.selectedPlace = null; render(); }
    if (action === "select-place") await selectPlace(Number(control.dataset.index));
    if (action === "clear-place") { ui.selectedPlace = null; ui.modal = null; render(); focusRestaurantInput(); }
    if (action === "suggest-change") { ui.modal = `suggest-change:${control.dataset.id}`; render(); }
    if (action === "update-booking") { ui.modal = `update-booking:${control.dataset.id}`; render(); }
    if (action === "cancel-plan") await cancelPlan(control.dataset.id!);
    if (action === "complete-plan") await completePlan(control.dataset.id!);
    if (action === "calendar") await downloadCalendar(control.dataset.id!);
    if (action === "review") { ui.modal = `review:${control.dataset.id}`; render(); }
    if (action === "reaction") { const input = document.querySelector<HTMLInputElement>("#review-reaction"); if (input) input.value = control.dataset.value || ""; document.querySelectorAll<HTMLElement>('[data-action="reaction"]').forEach((item) => item.classList.toggle("active", item === control)); }
    if (action === "delete-restaurant") await deleteRestaurant(control.dataset.id!);
  } catch (error) {
    toast(errorMessage(error), true);
  }
});

app.addEventListener("input", (event) => {
  const target = event.target as HTMLInputElement;
  if (target.id === "board-search") { ui.boardSearch = target.value; updateBoardResults(); }
  if (target.id === "restaurant-search") {
    ui.placeQuery = target.value;
    ui.selectedPlace = null;
    ui.placeResults = [];
    ui.placeSearchLoading = false;
    ui.placeSearchMessage = "";
    window.clearTimeout(placeSearchTimer);
    placeSearchSequence += 1;

    const input = ui.placeQuery.trim();
    const kind = classifyRestaurantInput(input);
    if (kind === "empty") {
      updatePlaceResults();
      return;
    }
    if (kind === "google-maps-url") {
      void resolveRestaurantUrl(input);
      return;
    }
    if (kind === "url") {
      ui.placeSearchMessage = "Paste an HTTPS Google Maps link or search by restaurant name.";
      updatePlaceResults();
      return;
    }
    if (input.length < 2) {
      ui.placeSearchMessage = "Type at least two characters.";
      updatePlaceResults();
      return;
    }
    if (!partnership()?.defaultCity?.trim()) {
      ui.placeSearchMessage = "Set this table’s default city once, then name searches will use it automatically.";
      updatePlaceResults();
      return;
    }
    updatePlaceResults();
    placeSearchTimer = window.setTimeout(() => void runPlaceSearch(), 350);
  }
});

app.addEventListener("submit", async (event) => {
  const form = (event.target as Element).closest<HTMLFormElement>("form[data-form]");
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (button) button.disabled = true;
  try {
    const data = formObject(form);
    if (form.dataset.form === "email") {
      const email = String(data.email).trim();
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
      if (error) throw appError("We couldn’t send a code. Wait a moment and try again.", error.code);
      ui.pendingEmail = email; ui.authStep = "code"; render(); toast("Check your email for a six-digit code.");
    }
    if (form.dataset.form === "otp") {
      const { data: verified, error } = await supabase.auth.verifyOtp({ email: ui.pendingEmail, token: String(data.code), type: "email" });
      if (error || !verified.session) throw appError("That code is invalid or expired. Request a new one and try again.", error?.code);
      session = verified.session; await loadState();
    }
    if (form.dataset.form === "table-auth" || form.dataset.form === "join-invitation") {
      const joining = form.dataset.form === "join-invitation" || ui.authMode === "join";
      const result = await request(joining ? "/api/join" : "/api/partnerships", { body: JSON.stringify(data) });
      model = result.state; resetTableUi(); rememberTable(result.tableId); render();
      if (result.alreadyShared) toast("You already share a table with this person, so we opened it.");
    }
    if (form.dataset.form === "restaurant") await handleRestaurant(form);
    if (form.dataset.form === "default-city") await handleDefaultCity(form);
    if (form.dataset.form === "create-plan") await handleCreatePlan(form);
    if (form.dataset.form === "booking") await handleBooking(form);
    if (form.dataset.form === "update-booking") await handleUpdatedBooking(form);
    if (form.dataset.form === "suggest-change") await handleBookingChange(form);
    if (form.dataset.form === "review") await handleReview(form);
  } catch (error) {
    toast(errorMessage(error), true);
    if (button) button.disabled = false;
  }
});

async function bootstrap() {
  render();
  const { data } = await supabase.auth.getSession();
  session = data.session;
  await loadState();
  supabase.auth.onAuthStateChange((event, nextSession) => {
    const previousUserId = session?.user.id;
    session = nextSession;
    if (!nextSession) {
      model = null; ui.authStep = "email"; ui.pendingEmail = ""; render();
    } else if (event === "SIGNED_IN" && previousUserId !== nextSession.user.id) {
      model = null; ui.loading = true; render();
      window.setTimeout(() => void loadState(), 0);
    }
  });
}

void bootstrap();
setInterval(() => { if (session && model && !document.hidden && !ui.modal) void loadState(true); }, 15_000);
window.addEventListener("popstate", () => { if (session) { resetTableUi(); void loadState(false, tableIdFromPath()); } });
